import { Component,Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { GeoObjPolygonTypesData, NetworkConstants } from 'diwimamodelweb/lib';
import { deepCopy, removeUndefined } from 'diwimamodelweb/lib/utils/obejct-diff';
import { gOPolygonTypesSettingForm } from '../model/form';
import { UserFeedbackService } from '../user-feedback.service';
import { validColorValidator } from 'ngx-colors';
import { firstValueFrom, skipWhile, take } from 'rxjs';
import { SettingsService } from '../settings.service';

@Component({
  selector: 'app-geo-object-polygon-types-setting-form',
  templateUrl: './geo-object-polygon-types-setting-form.component.html',
  styleUrls: ['./geo-object-polygon-types-setting-form.component.css', '../../app.component.scss']
})
export class GeoObjectPolygonTypesSettingFormComponent implements OnInit {

  @Input() data: gOPolygonTypesSettingForm
  @Input() formBuilder: FormBuilder
  @Input() translateService: TranslateService

  deleteRight = false
  updateRight = false
  gOPolygonTypesForm: FormGroup
  isCreate=false
  oldGeoObjectLine: GeoObjPolygonTypesData
  newChanges =false
  newBordervalue:string =''
  newFillvalue:string =''
  borderColorNameData:string
  fillColorNameData:string

  constructor(
    private userFeedbackService: UserFeedbackService,
    private settingsService: SettingsService
  ) { }

  async ngOnInit(): Promise<void> {

    const context = await firstValueFrom(this.settingsService.selectedUserContext$.pipe(
      skipWhile(el => !el),
      take(1)
    ))
    const rights = this.settingsService.rightsPercontext.get(context.identifier)
    this.updateRight = !rights?.[NetworkConstants.ROUTE_CTX_SETTINGS]?.['U'] ?? false
    this.deleteRight = !rights?.[NetworkConstants.ROUTE_CTX_SETTINGS]?.['D'] ?? false

    if (!this.data.data) this.isCreate = true
    this.gOPolygonTypesForm = this.formBuilder.group({
      title: ['', Validators.required],
      borderColorName: ['',validColorValidator()],
      borderWidth: [0,[Validators.required,Validators.min(0)]],
      fillColorName: ['',validColorValidator()],
      additionalObjects: [false],
      testInterval: [false],
      usageInformation: [false],
      notificationChannel: [false]
    });

    if(this.data.data) {
      this.isCreate=false
      this.oldGeoObjectLine = deepCopy(this.data.data)
      this.initilizeWithData()
    }

    this.gOPolygonTypesForm.valueChanges.subscribe( (value) =>{
      this.fillColorNameData = value.fillColorName
      this.borderColorNameData = value.borderColorName
      if(!this.isCreate){
        const {title, borderColorName, borderOpacity, borderWidth,fillColorName,fillOpacity, testinterval, usageInformation} = value
        let anyObj = {
          title, borderColorName, borderOpacity, borderWidth,fillColorName,fillOpacity, testinterval, usageInformation
        }
        this.newChanges=true
      }
    })
  }

  numberToHex(n,s){
    switch(s){
       case("border"): {
        if(n<1){
          const nhex=n.toString(16)
          this.newBordervalue=nhex.slice(2,4) 
        }
        if(n==1){
          this.newBordervalue=""
        }
        if(n==0){
          this.newBordervalue="00"
        }
        break
       }

       case("fill"): {
        if(n<1){
          const nhex=n.toString(16)
          this.newFillvalue=nhex.slice(2,4) 
        }
        if(n==1){
          this.newFillvalue=""
        }
        if(n==0){
          this.newFillvalue="00"
        }
        break
       }
    }
       
  }


  initilizeWithData(){
    this.borderColorNameData=this.data.data.borderColorName
    this.fillColorNameData=this.data.data.fillColorName

    const {title, borderWidth,fillOpacity }= this.data.data
    var borderColorName = this.data.data.borderColorName
    var fillColorName = this.data.data.fillColorName
    const {usageInformation, testInterval, notificationChannel } = this.data.data.displayOptions


    this.gOPolygonTypesForm.patchValue({
      title,
      borderColorName,
      borderWidth: Number.isFinite(Number(borderWidth))
        ? Number(borderWidth)
        : 0,
      fillColorName,
      usageInformation,
      testInterval,
      notificationChannel
    });
  }

  onSubmit(): void {
    if (this.gOPolygonTypesForm.invalid) {
      this.gOPolygonTypesForm.markAllAsTouched();
      return;
    }

    const {
      title,
      borderColorName,
      borderWidth,
      fillColorName,
      usageInformation,
      testInterval,
      notificationChannel
    } = this.gOPolygonTypesForm.getRawValue();

    const borderWidthNumber = Number(borderWidth);

    if (!Number.isFinite(borderWidthNumber) || borderWidthNumber < 0) {
      this.gOPolygonTypesForm
        .get('borderWidth')
        ?.setErrors({ invalidBorderWidth: true });

      return;
    }

    const displayOptions = {
      usageInformation,
      testInterval,
      notificationChannel
    };

    if (this.isCreate) {
      const setting = {
        title: title.trim(),
        identifier: title.trim().toLowerCase(),
        borderColorName,
        borderWidth: borderWidthNumber,
        fillColorName,
        displayOptions
      };

      const cleaned = removeUndefined(setting);


      this.data.onCreate(cleaned);
      return;
    }

    this.data.data.title = title.trim();
    this.data.data.borderColorName = borderColorName;
    this.data.data.borderWidth = borderWidthNumber;
    this.data.data.fillColorName = fillColorName;
    this.data.data.displayOptions = displayOptions;

    const cleaned = removeUndefined(this.data.data);
    this.data.onSubmit();
  }

  async onDelete(){
    if(!this.isCreate){
      this.data.onDelete(this.data)
    }else{
      const sbChangesDismissed = this.translateService.instant('delete_not_possible')
      this.userFeedbackService.showSnackBar.emit(sbChangesDismissed)                 
    }
  }
  
  lessBorderWidth(): void {
    const control = this.gOPolygonTypesForm.get('borderWidth');
    const current = Number(control?.value);

    control?.setValue(
      Math.max(0, Number.isFinite(current) ? current - 1 : 0)
    );
  }

  addBorderWidth(): void {
    const control = this.gOPolygonTypesForm.get('borderWidth');
    const current = Number(control?.value);

    control?.setValue(
      Number.isFinite(current) ? current + 1 : 1
    );
  }

  lessOpacity(){
    var nOpacity = Number(this.gOPolygonTypesForm.value.fillOpacity)

    if(nOpacity>0){
      nOpacity-=0.10
      const nOpacityRound= nOpacity.toFixed(1)
      this.gOPolygonTypesForm.get("fillOpacity").setValue(nOpacityRound.toString())
    }    
  }

  moreOpacity(){
    var nOpacity = Number(this.gOPolygonTypesForm.value.fillOpacity)
    
    if(nOpacity<1.0){
      nOpacity+=0.10
      const nOpacityRound= nOpacity.toFixed(1)
      this.gOPolygonTypesForm.get("fillOpacity").setValue(nOpacityRound.toString())
    }
  }
}